import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { GET, POST, httpOk } from 'helpers/api'
import DrawingModal from 'pages/main/cashbox/DrawingModal'

/**
 * AKSESSUAR CHIZMALARI — standart shakllar bitta ro'yxatda
 *
 * Chizma MAHSULOTNING xossasi: bu yerda (yoki mahsulot kartochkasida) bir marta
 * chiziladi, sotuvda esa nom tanlanishi bilan chek qatoriga O'ZI tushadi.
 *
 * ⚠️ Standart mahsulotda chizma NARXGA TEGMAYDI — u ustaga topshiriq va chekdagi
 *    rasm uchun. Narx katalogdagi «Кенглиги» dan chiqadi. Nostandartda esa
 *    aksincha: kenglikni chizma belgilaydi (`products.needs_drawing`).
 */
function AccessoryDrawings() {
	const { t } = useTranslation()

	const [items, setItems] = useState([])      // aksessuar + tunikofon, chizmasi bilan
	const [search, setSearch] = useState('')
	const [only, setOnly] = useState('all')     // all | with | without
	/* Mahsulot turi: 3 = aksessuar (sukut bo'yicha), 5 = tunikofon.
	   Ikkovi bitta ro'yxatda turadi, lekin ARALASHIB ketmasin — sotuvchi
	   odatda bittasi bilan ishlaydi (2026-08-11, foydalanuvchi talabi). */
	const [ptype, setPtype] = useState(3)
	const [edit, setEdit] = useState(null)      // chizilayotgan mahsulot

	async function getData() {
		const [prod, tpl] = await Promise.all([
			GET('/helper/products', {}, { loader: true }),
			GET('/accessory-templates'),
		])
		if (!httpOk(prod)) return
		const templates = httpOk(tpl) ? (tpl.data || []) : []
		/* AKSESSUAR (tur 3) va TUNIKOFON (tur 5) — ikkalasi ham shu ro'yxatda.
		   Ikkovi bir xil chizma tizimida ishlaydi, faqat narx modeli boshqa
		   (aksessuar rulon-metrda, tunikofon kv.metrda) — chizmaga bu ta'sir
		   qilmaydi (2026-08-11). */
		const acc = [
			...(prod.data?.accessories || []),
			...(prod.data?.tunicophones || []),
		].filter(x => !x.is_prepared)
		setItems(acc.map(p => ({
			...p,
			tpl: templates.find(x => Number(x.product_id) === Number(p.product_id)) || null,
		})))
	}

	async function save(payload) {
		const response = await POST('/accessory-templates', {
			name: edit.name,
			group_name: null,
			product_id: edit.product_id,
			draw_json: payload.draw_json,
			draw_width: payload.draw_width,
			draw_png: payload.draw_png,
		}, { loader: true })
		if (httpOk(response)) {
			toast.success(t('success'))
			getData()
		}
		setEdit(null)
	}

	useEffect(() => { getData() }, []) // eslint-disable-line react-hooks/exhaustive-deps

	const byType = items.filter(x => Number(x.product_type) === ptype)
	const rows = byType
		.filter(x => !search || String(x.name || '').toLowerCase().includes(search.toLowerCase()))
		.filter(x => only === 'all' || (only === 'with' ? !!x.tpl : !x.tpl))
	const countOf = tur => items.filter(x => Number(x.product_type) === tur).length
	// «Чизма бор» sanоg'i faqat KO'RINAYOTGAN tur bo'yicha — aks holda
	// tunikofonga o'tganda aksessuarning soni ko'rinib turardi
	const withCount = byType.filter(x => x.tpl).length

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">{t('accessory_drawings')}</h4>
			</div>

			<div className="card">
				<div className="card-body">
					{/* MAHSULOT TURI — birinchi va eng katta filtr. Aksessuar sukut
					    bo'yicha ochiladi, tunikofon alohida ko'riladi. */}
					<div className="btn-group mb-3">
						{[[3, t('accessory')], [5, t('tunikofon')]].map(([tur, label]) => (
							<button key={tur} type="button"
								className={'btn ' + (ptype === tur ? 'btn-primary' : 'btn-outline-primary')}
								onClick={() => { setPtype(tur); setOnly('all'); setSearch('') }}>
								{label} <span className="ms-1 opacity-75">({countOf(tur)})</span>
							</button>
						))}
					</div>

					<div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
						<div className="position-relative" style={{ maxWidth: '300px' }}>
							<i className="uil-search input-search-icon" />
							<input type="text" className="form-control ps-4" placeholder={t('search')}
								value={search} onChange={e => setSearch(e.target.value)} />
						</div>
						<div className="btn-group btn-group-sm">
							{[['all', t('all')], ['with', t('drawing')], ['without', t('no_drawing')]].map(([k, label]) => (
								<button key={k} type="button"
									className={'btn ' + (only === k ? 'btn-primary' : 'btn-outline-secondary')}
									onClick={() => setOnly(k)}>{label}</button>
							))}
						</div>
						<span className="text-muted" style={{ fontSize: '13px' }}>
							{t('drawing')}: <b>{withCount}</b> / {byType.length}
						</span>
					</div>

					<div className="table-responsive">
						<table className="table table-striped mb-0">
							<thead>
								<tr>
									<th style={{ width: '40px' }}>#</th>
									<th>{t('name')}</th>
									<th className="text-center" style={{ width: '130px' }}>{t('drawing')}</th>
									<th className="text-end" style={{ width: '120px' }}>{t('stretch_out')}</th>
									<th className="text-end" style={{ width: '120px' }}>{t('width')}</th>
									<th style={{ width: '110px' }}></th>
								</tr>
							</thead>
							<tbody>
								{rows.map((item, index) => (
									<tr key={item.product_id}>
										<td>{index + 1}</td>
										<td>
											{item.name}
											{item.needs_drawing &&
												<span className="text-warning ms-2" style={{ fontSize: '11.5px' }}>
													({t('needs_drawing')})
												</span>}
										</td>
										<td className="text-center">
											{item.tpl?.draw_png
												? <img src={item.tpl.draw_png} alt=""
													style={{ width: '84px', height: '46px', objectFit: 'contain' }} />
												: <span className="text-muted">—</span>}
										</td>
										<td className="text-end">
											{item.tpl?.draw_json?.blank ? `${item.tpl.draw_json.blank} ${t('mm')}` : '—'}
										</td>
										<td className="text-end">
											{item.width ? `${Math.round(item.width * 1000)} ${t('mm')}` : '—'}
										</td>
										<td className="text-end">
											<button type="button" className="btn btn-sm btn-outline-primary"
												onClick={() => setEdit(item)}>
												✎ {item.tpl ? t('edit') : t('draw')}
											</button>
										</td>
									</tr>
								))}
								{!rows.length &&
									<tr><td colSpan="6" className="text-center text-muted py-4">{t('no_data')}</td></tr>}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Shablon rejimi: rulon/hajm/narx yo'q, faqat shakl */}
			<DrawingModal
				show={!!edit}
				onHide={() => setEdit(null)}
				onSave={save}
				initial={edit?.tpl?.draw_json || null}
				rollWidthM={Number(edit?.length) || 1.25}
				rollPriceSom={0} markup={0}
				elementQuantity={0} quantity={0}
				rollRal={null} rollName={null}
				productId={edit?.product_id} rollProductId={null}
				rollCats={[]} needsDrawing={false} markupTiers={null}
				rollSel={null} onRollSel={() => {}} onOpenRollPicker={() => {}}
				productType={edit?.product_type}
				templateMode={true} />
		</>
	)
}

export default AccessoryDrawings
